/****************************************
 *
 * 副作用を伴う処理はcustom hookで定義している
 * @/hooks/auth-handler.tsx
 *
 ****************************************/
import {
	AuthError,
	confirmResetPassword,
	confirmSignIn,
	confirmSignUp,
	fetchAuthSession,
	fetchMFAPreference,
	getCurrentUser,
	resetPassword,
	signIn,
	signOut,
	signUp,
	updateMFAPreference,
} from "aws-amplify/auth";

import type {
	CognitoAccountDuplicateRequestMetadata,
	CognitoForgotPasswordRequestMetadata,
	CognitoSignUpRequestMetadata,
} from "../gql/__generated__/graphql";
import type {
	ConfirmSignUpOutput,
	ResetPasswordOutput,
	SignInOutput,
	SignUpOutput,
	UpdateMFAPreferenceInput,
} from "aws-amplify/auth";

export const isAuthError = (error: unknown) => {
	return error instanceof AuthError;
};

export const isSignIn = async () => {
	try {
		await getCurrentUser();
		return true;
	} catch (error) {
		return false;
	}
};

/**
 * Signs a user in
 *
 * @returns SignInOutput
 * @throws service: {@link InitiateAuthException }, {@link RespondToAuthChallengeException }
 *  - Cognito service errors thrown during the sign-in process.
 * @throws validation: {@link AuthValidationErrorCode  } - Validation errors thrown when either username or password
 *  are not defined.
 * @throws AuthTokenConfigException - Thrown when the token provider config is invalid.
 */
export const authSignIn = async (email: string, password: string): Promise<SignInOutput> => {
	return await signIn({ username: email, password });
};

export const authConfirmSignInMfaOtp = async (code: string) => {
	return await confirmSignIn({ challengeResponse: code });
};

export const authSignOut = async () => {
	await signOut();
};

/**
 * Resets a user's password.
 *
 * @returns ResetPasswordOutput
 * @throws -{@link ForgotPasswordException }
 * Thrown due to an invalid confirmation code or password.
 * @throws -{@link AuthValidationErrorCode }
 * Thrown due to an empty username.
 * @throws AuthTokenConfigException - Thrown when the token provider config is invalid.
 **/
export const authResetPassword = async (
	email: string,
	_clientMetadata: CognitoForgotPasswordRequestMetadata,
): Promise<ResetPasswordOutput> => {
	const clientMetadata = {
		callbackUrl: _clientMetadata.callbackUrl || "",
		codeParameter: _clientMetadata.codeParameter || "",
	} satisfies CognitoForgotPasswordRequestMetadata;
	return await resetPassword({ username: email, options: { clientMetadata } });
};

/**
 * Confirms the new password and verification code to reset the password.
 *
 * @param input -  The ConfirmResetPasswordInput object.
 * @throws -{@link ConfirmForgotPasswordException }
 * Thrown due to an invalid confirmation code or password.
 * @throws -{@link AuthValidationErrorCode }
 * Thrown due to an empty confirmation code, password or username.
 * @throws AuthTokenConfigException - Thrown when the token provider config is invalid.
 */
export const authConfirmResetPassword = async (
	email: string,
	password: string,
	confirmationCode: string,
): Promise<void> => {
	return confirmResetPassword({ username: email, newPassword: password, confirmationCode });
};

/**
 * Creates a user
 *
 * @param input - The SignUpInput object
 * @returns SignUpOutput
 * @throws service: {@link SignUpException } - Cognito service errors thrown during the sign-up process.
 * @throws validation: {@link AuthValidationErrorCode } - Validation errors thrown either username or password
 *  are not defined.
 * @throws AuthTokenConfigException - Thrown when the token provider config is invalid.
 */
export const authSignUp = async (
	email: string,
	password: string,
	_clientMetadata: CognitoSignUpRequestMetadata,
): Promise<SignUpOutput> => {
	const clientMetadata = {
		callbackUrl: _clientMetadata.callbackUrl || "",
		codeParameter: _clientMetadata.codeParameter || "",
		appendix: _clientMetadata.appendix || "",
		companyName: _clientMetadata.companyName || "",
		howDoYouKnow: _clientMetadata.howDoYouKnow || "",
		personInChargeName: _clientMetadata.personInChargeName || "",
		tel: _clientMetadata.tel || "",
	} satisfies CognitoSignUpRequestMetadata;
	return await signUp({ username: email, password, options: { userAttributes: {}, clientMetadata } });
};

/**
 * @description
 * system manager 経由の招待メールからのサインアップ
 */
export const authInvitationSignUp = async (
	email: string,
	password: string,
	_clientMetadata: CognitoAccountDuplicateRequestMetadata,
) => {
	const clientMetadata = {
		callbackUrl: _clientMetadata.callbackUrl || "",
		codeParameter: _clientMetadata.codeParameter || "",
		companyID: _clientMetadata.companyID || "",
		oneTimePass: _clientMetadata.oneTimePass || "",
	} satisfies CognitoAccountDuplicateRequestMetadata;

	return await signUp({ username: email, password, options: { userAttributes: {}, clientMetadata } });
};

/**
 * Confirms a new user account.
 *
 * @returns ConfirmSignUpOutput
 * @throws -{@link ConfirmSignUpException }
 * Thrown due to an invalid confirmation code.
 * @throws -{@link AuthValidationErrorCode }
 * Thrown due to an empty confirmation code
 * @throws AuthTokenConfigException - Thrown when the token provider config is invalid.
 */
export const authConfirmSignUp = async (
	email: string,
	confirmationCode: string,
	_clientMetaData?: { [key: string]: string },
): Promise<ConfirmSignUpOutput> => {
	return confirmSignUp({ username: email, confirmationCode, options: { clientMetadata: _clientMetaData } });
};

export const getIdToken = async () => {
	const { idToken } = (await fetchAuthSession()).tokens ?? {};
	return idToken?.toString();
};

export const authFetchMFAPreference = async () => {
	return await fetchMFAPreference();
};

export const authUpdateEmailMFAPreference = async (mfaPreference: Pick<UpdateMFAPreferenceInput, "email">) => {
	return await updateMFAPreference(mfaPreference);
};
